import type React from 'react';

import type * as api from '@gitbook/api-client';
import { PlainMarkdown } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    Alert,
    Link,
    PageTabAside,
    PageTabAsideSection,
    PageTabMain,
    PageTabPanel,
    Tooltip,
} from '@gitbook/spine-react';

import { useCurrentOrganizationId } from '../../../state';
import { getLabelsForScope } from '../utils';

/**
 * Panel showing an overview of information around an integration.
 */
export function IntegrationOverview(props: { integration: api.Integration }) {
    const { integration } = props;

    return (
        <PageTabPanel withAside>
            {integration.summary ? (
                <PageTabMain>
                    <PlainMarkdown input={integration.summary} />
                </PageTabMain>
            ) : null}

            <PageTabAside>
                <IntegrationVerifiedStatus
                    owner={integration.owner}
                    verified={integration.verified}
                />
                <IntegrationAuthor owner={integration.owner} />
                <IntegrationScopes scopes={integration.scopes} />
                <IntegrationLinks links={integration.externalLinks} />
            </PageTabAside>
        </PageTabPanel>
    );
}

/**
 * List of scopes required by an integration.
 */
function IntegrationScopes(props: { scopes: api.IntegrationScope[] }): React.ReactElement | null {
    const { scopes } = props;

    if (scopes.length === 0) {
        return null;
    }

    // Deduplicate and map scopes with their labels
    const uniqueScopesWithLabels = Array.from(new Set(scopes)).map((scope) => ({
        scope,
        labels: getLabelsForScope(scope),
    }));

    return (
        <PageTabAsideSection title="Permissions to">
            <ul className="flex flex-col gap-3">
                {uniqueScopesWithLabels.map(({ scope, labels }) => {
                    return (
                        <li key={scope} className="flex items-center gap-2">
                            <span className="text-muted text-ui-small">{labels.title}</span>

                            <Tooltip label={labels.description}>
                                <Icon.HelpCircle size={16} className="shrink-0 text-primary" />
                            </Tooltip>
                        </li>
                    );
                })}
            </ul>
        </PageTabAsideSection>
    );
}

/**
 * List of links to external resources for an integration.
 */
function IntegrationLinks(props: {
    links: api.Integration['externalLinks'];
}): React.ReactElement | null {
    const { links } = props;

    if (!links.length) {
        return null;
    }

    return (
        <PageTabAsideSection title="Links">
            <ul className="flex flex-col gap-3">
                {links.map(({ url, label }: { url: string; label: string }) => {
                    return (
                        <li key={url} className="flex items-center gap-2">
                            <Icon.ExternalLink size={18} className="shrink-0 text-primary" />

                            <Link
                                href={url}
                                target="_blank"
                                className="truncate text-muted text-ui-small"
                            >
                                {label}
                            </Link>
                        </li>
                    );
                })}
            </ul>
        </PageTabAsideSection>
    );
}

/**
 * Show the integration's author.
 */
function IntegrationAuthor(props: { owner: api.Integration['owner'] }): React.ReactElement | null {
    const { owner } = props;

    if (!owner) {
        return null;
    }

    return (
        <PageTabAsideSection title="Author">
            <div className="text-muted text-ui-small">{owner.title}</div>
        </PageTabAsideSection>
    );
}

/**
 * If the integration was not built by GitBook, show its verified status
 * and alert user of the risk of non-verified integrations.
 */
function IntegrationVerifiedStatus(props: {
    owner: api.Integration['owner'];
    verified: api.Integration['verified'];
}): React.ReactElement | null {
    const { owner, verified } = props;
    const organizationId = useCurrentOrganizationId();

    if (owner.hostname === 'gitbook') {
        return (
            <PageTabAsideSection>
                <Alert
                    kind="info"
                    size="small"
                    title="Maintained by GitBook"
                    icon={Icon.BadgeVerified}
                    details="This integration is developed and maintained by GitBook."
                />
            </PageTabAsideSection>
        );
    }

    if (verified) {
        return (
            <PageTabAsideSection>
                <Alert
                    kind="info"
                    size="small"
                    title="Verified by GitBook"
                    icon={Icon.BadgeVerified}
                    details="This integration has been checked by our team for security and reliability."
                />
            </PageTabAsideSection>
        );
    }

    const isOwner = organizationId && owner.id === organizationId;

    return (
        <PageTabAsideSection>
            <Alert
                kind="warning"
                size="small"
                title="Not verified by GitBook"
                details={
                    isOwner
                        ? 'This integration is not verified but you are the author. GitBook will need to verify it before you make it available to the public.'
                        : 'This integration has not been verified by our team. We cannot guarantee its security and reliability.'
                }
            />
        </PageTabAsideSection>
    );
}
